/**
 * QMS Gateways Section
 * Collapsible table for managing gateways on client tickets
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import type { QmsGateway } from '../../../types/qms.types';
import { useAddGateway, useUpdateGateway, useDeleteGateway } from '../../../hooks/useQmsSubResources';

interface QmsGatewaysSectionProps {
  ticketId: string;
  gateways: QmsGateway[];
}

const emptyGateway: Partial<QmsGateway> = {
  type: '',
  mac: '',
  ip: '',
  room: '',
  jack: '',
};

export function QmsGatewaysSection({ ticketId, gateways }: QmsGatewaysSectionProps) {
  const { t } = useTranslation('qms');
  const [expanded, setExpanded] = useState(gateways.length > 0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<QmsGateway>>({});
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState<Partial<QmsGateway>>(emptyGateway);

  const addMutation = useAddGateway(ticketId);
  const updateMutation = useUpdateGateway(ticketId);
  const deleteMutation = useDeleteGateway(ticketId);

  const handleAdd = async () => {
    try {
      await addMutation.mutateAsync(newItem);
      setIsAdding(false);
      setNewItem(emptyGateway);
      toast.success(t('subResources.gateways.added', 'Gateway added'));
    } catch {
      toast.error(t('subResources.gateways.addError', 'Failed to add gateway'));
    }
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    try {
      await updateMutation.mutateAsync({ gatewayId: editingId, data: editData });
      setEditingId(null);
      setEditData({});
      toast.success(t('subResources.gateways.updated', 'Gateway updated'));
    } catch {
      toast.error(t('subResources.gateways.updateError', 'Failed to update gateway'));
    }
  };

  const handleDelete = async (gatewayId: string) => {
    if (!confirm(t('subResources.confirmDelete', 'Delete this item?'))) return;
    try {
      await deleteMutation.mutateAsync(gatewayId);
      toast.success(t('subResources.gateways.deleted', 'Gateway deleted'));
    } catch {
      toast.error(t('subResources.gateways.deleteError', 'Failed to delete gateway'));
    }
  };

  const startEditing = (gw: QmsGateway) => {
    setEditingId(gw.id);
    setEditData({ type: gw.type, mac: gw.mac, ip: gw.ip, room: gw.room, jack: gw.jack });
  };

  return (
    <div className="border-t pt-4">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-base font-semibold text-gray-900 hover:text-atek-blue"
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {t('form.sections.gateways', 'Gateways')}
        <span className="text-sm font-normal text-gray-500">({gateways.length})</span>
      </button>

      {expanded && (
        <div className="mt-3">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.type', 'Type')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.mac', 'MAC')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.ip', 'IP')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.room', 'Room')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.jack', 'Jack')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {gateways.map((gw) => (
                  editingId === gw.id ? (
                    <tr key={gw.id} className="border-b border-gray-100 bg-blue-50">
                      <td className="px-2 py-1"><input type="text" value={editData.type || ''} onChange={(e) => setEditData({ ...editData, type: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1"><input type="text" value={editData.mac || ''} onChange={(e) => setEditData({ ...editData, mac: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1"><input type="text" value={editData.ip || ''} onChange={(e) => setEditData({ ...editData, ip: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1"><input type="text" value={editData.room || ''} onChange={(e) => setEditData({ ...editData, room: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1"><input type="text" value={editData.jack || ''} onChange={(e) => setEditData({ ...editData, jack: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1 flex gap-1">
                        <button type="button" onClick={handleUpdate} disabled={updateMutation.isPending} className="p-1 text-green-600 hover:bg-green-100 rounded"><Check className="h-4 w-4" /></button>
                        <button type="button" onClick={() => setEditingId(null)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={gw.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-900">{gw.type || '-'}</td>
                      <td className="px-3 py-2 text-gray-900 font-mono">{gw.mac || '-'}</td>
                      <td className="px-3 py-2 text-gray-900 font-mono">{gw.ip || '-'}</td>
                      <td className="px-3 py-2 text-gray-900">{gw.room || '-'}</td>
                      <td className="px-3 py-2 text-gray-900">{gw.jack || '-'}</td>
                      <td className="px-3 py-2 flex gap-1">
                        <button type="button" onClick={() => startEditing(gw)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><Pencil className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => handleDelete(gw.id)} disabled={deleteMutation.isPending} className="p-1 text-red-500 hover:bg-red-100 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    </tr>
                  )
                ))}
                {isAdding && (
                  <tr className="border-b border-gray-100 bg-green-50">
                    <td className="px-2 py-1"><input type="text" value={newItem.type || ''} onChange={(e) => setNewItem({ ...newItem, type: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" placeholder="Type" /></td>
                    <td className="px-2 py-1"><input type="text" value={newItem.mac || ''} onChange={(e) => setNewItem({ ...newItem, mac: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" placeholder="MAC" /></td>
                    <td className="px-2 py-1"><input type="text" value={newItem.ip || ''} onChange={(e) => setNewItem({ ...newItem, ip: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" placeholder="IP" /></td>
                    <td className="px-2 py-1"><input type="text" value={newItem.room || ''} onChange={(e) => setNewItem({ ...newItem, room: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" placeholder="Room" /></td>
                    <td className="px-2 py-1"><input type="text" value={newItem.jack || ''} onChange={(e) => setNewItem({ ...newItem, jack: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" placeholder="Jack" /></td>
                    <td className="px-2 py-1 flex gap-1">
                      <button type="button" onClick={handleAdd} disabled={addMutation.isPending} className="p-1 text-green-600 hover:bg-green-100 rounded"><Check className="h-4 w-4" /></button>
                      <button type="button" onClick={() => setIsAdding(false)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!isAdding && (
            <button type="button" onClick={() => setIsAdding(true)} className="mt-2 flex items-center gap-1 text-sm text-atek-blue hover:text-atek-blue-dark">
              <Plus className="h-4 w-4" /> {t('subResources.gateways.add', 'Add Gateway')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
